import { Project } from '../../services/projectService';

export function formatProjectDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function isPastEndDate(value?: string | null): boolean {
  if (!value) return false;
  const end = new Date(value);
  if (Number.isNaN(end.getTime())) return false;
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return today > endDay;
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function daysUntilEndDate(value?: string | null): number | null {
  if (!value || isPastEndDate(value)) return null;
  const end = new Date(value);
  if (Number.isNaN(end.getTime())) return null;
  const today = startOfLocalDay(new Date());
  return Math.round((startOfLocalDay(end).getTime() - today.getTime()) / MS_PER_DAY);
}

export function scheduleGap(project: Project): number | null {
  if (!project.startDate || !project.endDate) return null;
  const start = new Date(project.startDate);
  const end = new Date(project.endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const startMs = startOfLocalDay(start).getTime();
  const endMs = startOfLocalDay(end).getTime();
  if (endMs <= startMs) return null;
  const elapsedFrac = Math.min(1, Math.max(0, (startOfLocalDay(new Date()).getTime() - startMs) / (endMs - startMs)));
  return (project.taskCompletion?.percent ?? 0) / 100 - elapsedFrac;
}

export type ProjectCardTheme = 'critical' | 'warning' | 'neutral' | 'positive' | 'success';

export function getProjectCardTheme(project: Project): ProjectCardTheme {
  if (isPastEndDate(project.endDate)) return 'critical';
  const pct = project.taskCompletion?.percent ?? 0;
  if (pct >= 100) return 'success';

  const daysLeft = daysUntilEndDate(project.endDate);
  const gap = scheduleGap(project);

  if (gap !== null) {
    if (gap < -0.12) return 'warning';
    if (gap >= 0.1 && pct >= 35) return 'positive';
  }
  if (daysLeft !== null) {
    if (daysLeft <= 1 && pct < 98) return 'warning';
    if (daysLeft <= 7 && pct < 40) return 'warning';
    if (daysLeft <= 14 && pct < 20) return 'warning';
    if (daysLeft >= 21 && pct >= 70) return 'positive';
  }
  if (pct >= 85) return 'positive';
  return 'neutral';
}

export function getProjectStatusBadges(theme: ProjectCardTheme, project: Project) {
  const badges: Array<{ label: string; tone: string }> = [];
  if (theme === 'critical') badges.push({ label: 'Past end date', tone: 'critical' });
  if (theme === 'warning') badges.push({ label: 'At risk', tone: 'warning' });
  if (theme === 'success') badges.push({ label: 'Complete', tone: 'success' });
  badges.push({
    label: project.status === 'archived' ? 'Archived' : 'Active',
    tone: project.status === 'archived' ? 'muted' : 'success'
  });
  return badges;
}
